import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import ContentLoading from 'Components/ContentLoading';
import {
    CircularProgress,
    Checkbox,
    Grid,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import SearchBoxInput from 'Components/SearchBoxInput';
import { USER_ROLES } from 'utils/constants';
import variables from '../../../../Sys/variable.scss';
import { userCurrentProject, usersMovingProject } from './assignUsers';

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 8,
    },
    titleWrap: {
        padding: '20px 24px 8px',
    },
    title: {
        fontSize: 18,
        fontWeight: 600,
        color: variables.darkThemeText,
    },
    subtitle: {
        marginTop: 4,
        fontSize: 13,
        color: theme.palette.grey[600],
    },
    content: {
        paddingTop: 8,
    },
    tableContainer: {
        position: 'relative',
        marginTop: 16,
        maxHeight: 560,
        borderRadius: 8,
        border: '1px solid rgba(0,0,0,0.10)',
        overflow: 'auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    },
    headerCell: {
        color: 'var(--dm-text-strong, #263548)',
        fontWeight: 700,
        fontSize: '0.78rem',
        padding: '13px 16px',
        whiteSpace: 'nowrap',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        borderBottom: '1px solid var(--dm-border, #e7ebf0)',
        backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        '&.MuiTableCell-stickyHeader': {
            backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        },
    },
    checkCell: {
        width: 56,
        padding: '6px 8px 6px 16px',
        textAlign: 'center',
    },
    headerCheck: {
        padding: 2,
        color: 'var(--dm-muted, #647184)',
        backgroundColor: 'transparent',
        borderRadius: 2,
        '&:hover': {
            backgroundColor: 'rgba(61, 125, 221, 0.08)',
        },
        '& .MuiSvgIcon-root': {
            fontSize: 22,
        },
        '&.Mui-checked, &.MuiCheckbox-indeterminate': {
            color: variables.primaryColor,
        },
        '&.Mui-disabled': {
            color: 'rgba(100, 113, 132, 0.45)',
        },
    },
    rowCheck: {
        padding: 2,
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '& .MuiSvgIcon-root': {
            fontSize: 22,
        },
    },
    bodyRow: {
        '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(0,0,0,0.025)',
        },
        '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.055)',
        },
    },
    selectedRow: {
        backgroundColor: 'rgba(61, 125, 221, 0.08) !important',
    },
    bodyCell: {
        color: variables.darkThemeText,
        padding: '11px 16px',
        fontSize: '0.875rem',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
    },
    emptyCell: {
        padding: '36px 16px',
        textAlign: 'center',
        color: variables.darkThemeText,
        fontSize: '0.9rem',
        fontStyle: 'italic',
        borderBottom: 'none',
    },
    footerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 16,
    },
    pageSize: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        color: theme.palette.grey[700],
    },
    pageSizeSelect: {
        minWidth: 72,
    },
    actions: {
        padding: '8px 24px 20px',
        justifyContent: 'space-between',
    },
    selectedLabel: {
        fontSize: 13,
        color: theme.palette.grey[700],
    },
    actionButtons: {
        display: 'flex',
        gap: 12,
    },
    cancelBtn: {
        minWidth: 120,
    },
    submitBtn: {
        minWidth: 140,
    },
}));

export default function AssignUserModal({
    open,
    loading,
    handleClose,
    list,
    handleSubmit,
    submitLoading,
    handleSearch,
    handleChange,
    queryData,
    page,
    pageSize,
    total,
    onPageChange,
    onPageSizeChange,
    targetProjectId,
    projectNames = {},
}) {
    const classes = useStyles();
    const [selectedRows, setSelectedRows] = useState({});
    const [confirmMove, setConfirmMove] = useState(false);

    useEffect(() => {
        if (!open) {
            setSelectedRows({});
            setConfirmMove(false);
        }
    }, [open]);

    const rows = [];
    (Array.isArray(list) ? list : []).forEach(itm => {
        if (itm.userName && itm.role === USER_ROLES.USER) {
            rows.push({
                id: itm.uuid,
                firstName: itm.firstName,
                lastName: itm.lastName,
                role: itm.role,
                userName: itm.userName,
                email: itm.email,
                project: userCurrentProject(itm, projectNames),
                source: itm,
            });
        }
    });

    const selectedUsers = Object.keys(selectedRows);
    const moving = usersMovingProject(Object.values(selectedRows), targetProjectId, projectNames);
    const pageIds = rows.map(row => row.id);
    const selectedOnPage = pageIds.filter(id => selectedRows[id]);
    const allSelected = pageIds.length > 0 && selectedOnPage.length === pageIds.length;
    const pageCount = pageSize ? Math.ceil((total || 0) / pageSize) : 0;

    const toggleUser = (row) => {
        setConfirmMove(false);
        setSelectedRows(current => {
            const next = { ...current };
            if (next[row.id]) delete next[row.id];
            else next[row.id] = row.source;
            return next;
        });
    };

    const togglePage = () => {
        setConfirmMove(false);
        setSelectedRows(current => {
            const next = { ...current };
            rows.forEach(row => {
                if (allSelected) delete next[row.id];
                else next[row.id] = row.source;
            });
            return next;
        });
    };

    const submit = () => {
        if (moving.length && !confirmMove) {
            setConfirmMove(true);
            return;
        }
        handleSubmit(selectedUsers);
    };

    return (
        <Dialog
            fullWidth
            maxWidth="lg"
            open={open}
            onClose={handleClose}
            classes={{ paper: classes.paper }}
            aria-labelledby="assign-users-title"
        >
            <DialogTitle id="assign-users-title" disableTypography className={classes.titleWrap}>
                <Typography className={classes.title}>Assign existing users</Typography>
                <Typography className={classes.subtitle}>
                    Search this customer&apos;s users and select who to add to this project.
                </Typography>
            </DialogTitle>
            <DialogContent className={classes.content}>
                <Grid container>
                    <Grid item xs={12} sm={8} md={6}>
                        <SearchBoxInput
                            handleSearch={handleSearch}
                            handleChange={handleChange}
                            value={queryData?.search || ''}
                            placeholder="Search by name, username, or email"
                            styleOverrides={{ width: '100%', minWidth: 0 }}
                        />
                    </Grid>
                </Grid>
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={`${classes.headerCell} ${classes.checkCell}`} padding="none">
                                    <Checkbox
                                        className={classes.headerCheck}
                                        color="default"
                                        checked={allSelected}
                                        indeterminate={selectedOnPage.length > 0 && !allSelected}
                                        disabled={!rows.length || loading}
                                        onChange={togglePage}
                                        inputProps={{ 'aria-label': 'Select users on this page' }}
                                    />
                                </TableCell>
                                <TableCell className={classes.headerCell}>First name</TableCell>
                                <TableCell className={classes.headerCell}>Surname</TableCell>
                                <TableCell className={classes.headerCell}>Username</TableCell>
                                <TableCell className={classes.headerCell}>Email</TableCell>
                                <TableCell className={classes.headerCell}>User role</TableCell>
                                <TableCell className={classes.headerCell}>Current project</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} className={classes.emptyCell}>
                                        <ContentLoading columns={7} />
                                    </TableCell>
                                </TableRow>
                            ) : rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className={classes.emptyCell}>
                                        No users from this customer found.
                                    </TableCell>
                                </TableRow>
                            ) : rows.map(row => {
                                const selected = !!selectedRows[row.id];
                                const inThisProject = row.project.id && row.project.id === targetProjectId;
                                return (
                                    <TableRow
                                        key={row.id}
                                        hover
                                        className={`${classes.bodyRow} ${selected ? classes.selectedRow : ''}`}
                                        onClick={() => toggleUser(row)}
                                    >
                                        <TableCell className={`${classes.bodyCell} ${classes.checkCell}`} padding="none">
                                            <Checkbox
                                                className={classes.rowCheck}
                                                color="default"
                                                checked={selected}
                                                onChange={() => toggleUser(row)}
                                                onClick={(event) => event.stopPropagation()}
                                                inputProps={{ 'aria-label': `Select ${row.userName}` }}
                                            />
                                        </TableCell>
                                        <TableCell className={classes.bodyCell}>{row.firstName || '—'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{row.lastName || '—'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{row.userName}</TableCell>
                                        <TableCell className={classes.bodyCell}>{row.email || '—'}</TableCell>
                                        <TableCell className={classes.bodyCell}>{row.role}</TableCell>
                                        <TableCell className={classes.bodyCell}>
                                            {inThisProject ? 'This project' : (row.project.name || (row.project.id ? 'Another project' : '—'))}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <div className={classes.footerBar}>
                    <div className={classes.pageSize}>
                        Rows
                        <Select
                            variant="outlined"
                            margin="dense"
                            value={pageSize || 50}
                            className={classes.pageSizeSelect}
                            onChange={(event) => onPageSizeChange(Number(event.target.value))}
                        >
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </div>
                    {pageCount > 1 && (
                        <Pagination
                            page={page || 1}
                            count={pageCount}
                            onChange={(event, value) => onPageChange(value - 1)}
                            variant="outlined"
                            shape="rounded"
                            siblingCount={0}
                        />
                    )}
                </div>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Typography className={classes.selectedLabel} role={confirmMove ? 'alert' : undefined} style={confirmMove ? { color: '#b45309', fontWeight: 600 } : undefined}>
                    {confirmMove
                        ? `${moving.length} of the selected users will be moved out of their current project. Click Assign again to confirm.`
                        : (selectedUsers.length === 1 ? '1 user selected' : `${selectedUsers.length} users selected`)}
                </Typography>
                <div className={classes.actionButtons}>
                    <Button onClick={handleClose} variant="outlined" color="default" className={classes.cancelBtn}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        className={classes.submitBtn}
                        disabled={!selectedUsers.length || submitLoading}
                        onClick={submit}
                    >
                        {submitLoading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : (confirmMove ? 'Move and assign' : 'Assign')}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}
